import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import { useAppDispatch, useAppStore } from "@/global/store";
import {
  gameFiltersState,
  selectSections,
  selectSectionSelected,
} from "@/global/store/data/gameFilters";
import React from "react";
import { findMappedSection } from "./utils.seatMap";
import {
  filterPanelNames,
  inventoryTypeUIDs,
} from "@/global/config/constants";
import { useFilterPanelsState } from "../GameFilters/useFilterPanels";
import { useSelectActiveEvent } from "../PurchaseSeats/ChooseSeats/useSelectActiveEvent";
import { selectInventory } from "@/global/store/data/purchaseSeats/inventories";
import type { SectionName } from "@/global/store/data/types";

export function useSeatMap2d() {
  const dispatch = useAppDispatch();
  const teamConfig = useTeamConfig();
  const availableSections = useAppStore(selectSections);
  const selectedSection = useAppStore(selectSectionSelected);
  const { activeEvent } = useSelectActiveEvent();
  const inventories = useAppStore(selectInventory(activeEvent?.eventCode));
  const [viewer, setViewerInstance] = React.useState<any>(null);
  const { set: setFilterPanelsState } = useFilterPanelsState();

  // `getInventoryFilters` (which feeds `selectSections`) can return a section
  // list that disagrees with the inventory `getInventory` actually returns, so
  // sections visible in the grid ended up greyed out on the map. Derive the
  // sections from the loaded inventory as a second source and merge the two.
  //
  // NOTE: `selectInventory` is a selector factory and returns a brand new []
  // when the event has no inventory, so `inventories` cannot be used as an
  // effect dependency - compare by value through a stable key instead.
  const inventoryKey = React.useMemo(
    () =>
      (inventories || [])
        .map((inventory: any) => inventory?.sectionName)
        .filter(Boolean)
        .sort()
        .join("|"),
    [inventories]
  );

  // Selecting a section filters the inventory down to that section, so this
  // snapshot must only be refreshed while nothing is selected - otherwise the
  // map would collapse to the single selected node.
  const [inventorySections, setInventorySections] = React.useState<{
    key: string;
    sections: SectionName[];
  }>({ key: "", sections: [] });

  React.useEffect(() => {
    if (selectedSection) return;
    if (inventoryKey === inventorySections.key) return;
    setInventorySections({
      key: inventoryKey,
      sections: calcSectionsFromInventory(inventories),
    });
  }, [inventoryKey, selectedSection, inventorySections.key]);

  // Group-level sections from `getInventoryFilters` only mean "has >= seatCount
  // seats", while `getInventory` needs a connected block (and may cap the rows
  // it spans), so a highlighted section could load no inventory at all. Only
  // trust the sections that actually came back as inventory.
  const isGroupLevel = teamConfig?.inventoryType === inventoryTypeUIDs.groupLevel; // prettier-ignore

  // API sections come first so their original `sectionNames` payload wins
  const mapSections = React.useMemo<SectionName[]>(() => {
    const byMapSectionName = new Map<string, SectionName>();
    const apiSections = isGroupLevel ? [] : availableSections || [];

    [...apiSections, ...inventorySections.sections].forEach(
      (section) => {
        const mapSectionName = section?.mapSectionName;
        if (!mapSectionName || byMapSectionName.has(mapSectionName)) return;
        byMapSectionName.set(mapSectionName, section);
      }
    );

    return Array.from(byMapSectionName.values());
  }, [availableSections, inventorySections, isGroupLevel]);

  const setViewerAvailability = () => {
    if (!viewer || !mapSections?.length) return;

    const sectionIds = mapSections.map((apiSection) => {
      const sectionName = findMappedSection(teamConfig, apiSection);
      let sectionId = "S_" + sectionName?.mapSectionName;
      return sectionId;
    });

    if (!sectionIds?.length) return;

    // Not every backend section has a node on the map (e.g. codes with no
    // sectionNameMapping entry). Passing an unknown id can make the viewer
    // reject the whole batch, which would leave the map completely greyed out.
    const nodeIds = new Set<string>(
      (viewer?.getNodes?.() || []).map((node: any) => node?.id)
    );
    const knownSectionIds = nodeIds.size
      ? sectionIds.filter((id) => nodeIds.has(id))
      : sectionIds;

    if (!knownSectionIds.length) return;
    viewer?.setAvailability("section", knownSectionIds);
  };

  const setSelectedSection = (node: any) => {
    if (!node || !node?.id) return;

    let apiSectionName = null;
    mapSections?.forEach((apiSection) => {
      let sectionId = node?.id?.replace("S_", "");
      const mappedSection = findMappedSection(teamConfig, apiSection);
      if (mappedSection?.mapSectionName == sectionId) {
        apiSectionName = mappedSection;
      }
    });
    if (!apiSectionName) return;

    dispatch(gameFiltersState.actions.updateSectionSelected(apiSectionName));
    setFilterPanelsState({
      lastChangedFilter: filterPanelNames.section_selector,
    });
  };

  const resetSelectedSection = () => {
    let selectedNodes = viewer?.getNodesByTag("section", "selected");
    viewer?.setNodesTag(selectedNodes, null);

    dispatch(gameFiltersState.actions.updateSectionSelected(null));
    setFilterPanelsState({
      lastChangedFilter: filterPanelNames.section_selector,
    });
  };

  return {
    viewer,
    mapSections,
    setViewerInstance,
    setViewerAvailability,
    setSelectedSection,
    resetSelectedSection,
  };
}

function calcSectionsFromInventory(inventories: any[]): SectionName[] {
  const byMapSectionName = new Map<string, SectionName>();

  (inventories || []).forEach((inventory: any) => {
    const sectionName = inventory?.sectionName;
    if (!sectionName) return;

    // group-level inventory does not carry mapSectionName, only sectionName
    const mapSectionName = inventory?.mapSectionName || sectionName;
    if (byMapSectionName.has(mapSectionName)) return;

    byMapSectionName.set(mapSectionName, {
      displaySectionName: inventory?.displaySectionName || sectionName,
      mapSectionName,
      sectionNames: [sectionName],
    });
  });

  return Array.from(byMapSectionName.values());
}
